import Editor from '@/components/tiptap/Editor';
import api from '@/services/ApiService';
import {
  ArrowBack,
  Checklist,
  DocumentScannerOutlined,
  Edit,
  MailOutline,
  TaskAltOutlined,
  Timeline,
} from '@mui/icons-material';
import { TabContext, TabPanel } from '@mui/lab';
import {
  alpha,
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  LinearProgress,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';

const request = {
  _id: 1,
  title: 'Weiterleitung bei besetzt',
  status: 'new',
  owner: '6772997a8194537ef3121e2d',
  customerName: 'Hees und Peters',
  customerId: '690336bc427edd5aba770123',
  estimatedHours: 2,
  bookedHours: 1.5,
  team: ['647eefc735edfc0d3ebe682f', '6772997a8194537ef3121e2d'],
  dueAt: new Date('2026-01-19'),
  createdAt: new Date('2026-01-12'),
  summary: 'Herr Hött hat eine Nachfrage zur Darstellung eines Anrufs im Callcenter-Tool.',
  reportedBy: '68f0f988686d9552bae318e6'
};

const statusTitles = {
  new: 'Neu',
  active: 'In Bearbeitung',
  completed: 'Abgeschlossen',
};

const statusColors = {
  new: 'info',
  active: 'grey',
  completed: 'success',
};

export default function SupportRequest() {
  const { users } = useRouteLoaderData('auth');
  const [tab, setTab] = useState('overview');
  const [reportedBy, setReportedBy] = useState(null);
  const [customer, setCustomer] = useState(null);

  const owner = users.find((u) => u._id === request.owner);
  const team = request.team.map((memberId) =>
    users.find((u) => u._id === memberId),
  );

  useEffect(() => {
    (async () => {
      const { data: reporter } = await api.get(`/party/${request.reportedBy}`);
      setReportedBy(reporter);

      const { data: customerData } = await api.get(`/party/${request.customerId}`);
      setCustomer(customerData);
    })();
  }, []);

  const populatedRequest = { ...request, owner, team, reportedBy, customer };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '1600px',
        maxWidth: '100%',
        height: '100%',
        justifyContent: 'center',
        flexDirection: 'row',
        alignSelf: 'center',
        justifySelf: 'center',
        p: 2,
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '300px',
        }}
      >
        <Card
          sx={{
            p: 2,
            height: 'fit-content',
            width: '100%',
            position: 'relative',
          }}
        >
          <Button
            sx={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              minWidth: '32px',
            }}
          >
            <Edit sx={{ fontSize: '18px' }} />
          </Button>
          <Chip
            label={statusTitles[request.status]}
            color={statusColors[request.status]}
            sx={{ height: '26px' }}
          />
          <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
            {request.title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              Kunde
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              {populatedRequest.customer?.company}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              Erstellt am
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              {format(populatedRequest.createdAt, 'dd.MM.yyyy HH:mm')} Uhr
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              von
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              {`${populatedRequest.reportedBy?.firstName} ${populatedRequest.reportedBy?.lastName}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              Fälligkeit
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              {format(populatedRequest.dueAt, 'dd.MM.yyyy HH:mm')} Uhr
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              Erfasste Zeit
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              {populatedRequest.bookedHours} Stunden ({populatedRequest.estimatedHours}h geplant)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
                Gesamtfortschritt
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  mb: 0.5,
                  textAlign: 'right',
                }}
              >
                {((populatedRequest.bookedHours / populatedRequest.estimatedHours) * 100).toFixed(
                  2,
                )}
                %
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(populatedRequest.bookedHours / populatedRequest.estimatedHours) * 100 || 0}
              color="info"
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              Team
            </Typography>
            <AvatarGroup
              sx={{ mt: 0.5, justifyContent: 'flex-end', ml: 2 }}
              fullWidth
              max={8}
              spacing={16}
            >
              {populatedRequest.team.map((member, index) => {
                return (
                  <Tooltip
                    key={member._id}
                    title={`${member.firstName} ${member.lastName}`}

                  >
                    { index === 0 ? <Badge badgeContent={1} overlap="circular" color="primary">
                      <Avatar
                        alt={`${member.firstName} ${member.lastName}`}
                        src={member.profilePictureUrl}
                      />
                    </Badge> : <Avatar
                      alt={`${member.firstName} ${member.lastName}`}
                      src={member.profilePictureUrl}
                    /> }
                  </Tooltip>
                );
              })}
            </AvatarGroup>
          </Box>
        </Card>
        <Button fullWidth startIcon={<ArrowBack />} to="/support">
          Zurück zur Übersicht
        </Button>
      </Box>
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <TabContext value={tab}>
          <Tabs
            value={tab}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: '48px',
              '& .MuiTab-root': {
                minHeight: '48px',
                py: 1,
              },
            }}
            variant="fullWidth"
            onChange={(event, newValue) => setTab(newValue)}
          >
            <Tab
              label="Übersicht"
              icon={<Checklist sx={{ fontSize: '18px' }} />}
              iconPosition="start"
              value="overview"
            />
            <Tab
              label="Aufgabenplanung"
              icon={<TaskAltOutlined sx={{ fontSize: '18px' }} />}
              iconPosition="start"
              value="timeTracking"
            />
            <Tab
              label="Kommunikation"
              icon={<MailOutline sx={{ fontSize: '18px' }} />}
              iconPosition="start"
              value="invoices"
            />
            <Tab
              label="Dokumentation"
              icon={<DocumentScannerOutlined sx={{ fontSize: '18px' }} />}
              iconPosition="start"
              value="files"
            />
            <Tab
              label="Aktivitäten"
              icon={<Timeline sx={{ fontSize: '18px' }} />}
              iconPosition="start"
              value="settings"
            />
          </Tabs>
          <TabPanel value="overview" sx={{ display: 'flex', gap: 2, flex: 1 }}>
            <Box
              sx={{ flex: 1, gap: 2, display: 'flex', flexDirection: 'column' }}
            >
              <Card
                sx={{
                  p: 2,
                  boxShadow: 'none',
                  backgroundColor: (theme) =>
                    alpha(theme.palette.info.main, 0.1),
                  flex: 1,
                  overflow: 'auto'
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    fontSize: 'h6.fontSize',
                    mb: 1,
                  }}
                >
                  📌 Zusammenfassung
                </Typography>
                <Editor content={populatedRequest.summary} editable={false} />
              </Card>
            </Box>
            <Box sx={{ width: '450px', maxWidth: '50%', p: 2 }}>
              <Typography
                  sx={{
                    fontWeight: 'bold',
                    fontSize: 'h6.fontSize',
                    mb: 1,
                  }}
                >
                  📈 Aktivitäten
                </Typography>
            </Box>
          </TabPanel>
          <TabPanel value="timeTracking">
            <Typography>Aufgabenplanung</Typography>
          </TabPanel>
          <TabPanel value="invoices">
            <Typography>Kommunikation</Typography>
          </TabPanel>
          <TabPanel value="files">
            <Typography>Dokumentation</Typography>
          </TabPanel>
          <TabPanel value="settings">
            <Typography>Aktivitäten</Typography>
          </TabPanel>
        </TabContext>
      </Card>
    </Box>
  );
}
